import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Clock, CheckCircle, XCircle, CreditCard, Mail, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/SEOHead";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogFooter from "@/components/blog/BlogFooter";

const PoliticaReembolso = () => {
  return (
    <>
      <SEOHead
        title="Política de Reembolso | Magnet Link"
        description="Conheça nossa política de reembolso. Transparência e garantia de satisfação para todos os nossos clientes."
        keywords="reembolso, devolução, garantia, cancelamento, política"
        canonicalUrl="/politica-de-reembolso"
      />

      <div className="min-h-screen bg-background">
        <BlogHeader />

        {/* Header */}
        <header className="pt-32 pb-12 md:pt-40 md:pb-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <nav 
              className="mb-8"
              aria-label="Navegação de retorno"
            >
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Voltar ao Início
                </Link>
              </Button>
            </nav>

            <div className="mx-auto max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <RefreshCw className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
                  Política de Reembolso
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Última atualização: Janeiro de 2025
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <main role="main" aria-label="Política de Reembolso">
          <article className="py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-4xl">
                
                {/* Garantia de Satisfação */}
                <section aria-labelledby="garantia" className="mb-12 not-prose">
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="p-8">
                      <div className="flex flex-col md:flex-row items-center gap-6">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/20 flex-shrink-0">
                          <CheckCircle className="h-10 w-10 text-primary" aria-hidden="true" />
                        </div>
                        <div>
                          <h2 id="garantia" className="text-2xl font-bold mb-2">Garantia de 7 Dias</h2>
                          <p className="text-muted-foreground">
                            Se você não estiver satisfeito com o Magnet Link, pode solicitar reembolso 
                            total em até 7 dias após a primeira cobrança do plano Pro. Sem perguntas, 
                            sem burocracia. Sua satisfação é nossa prioridade.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>

                {/* Quando Você Pode Solicitar */}
                <section aria-labelledby="quando" className="mb-12">
                  <h2 id="quando" className="text-2xl font-bold mb-6">Quando Você Pode Solicitar Reembolso</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="border-green-500/30 bg-green-500/5">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <CheckCircle className="h-6 w-6 text-green-600" aria-hidden="true" />
                          <h3 className="font-semibold text-green-600">Elegível para Reembolso</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <span className="text-green-600">✓</span>
                            Solicitação dentro de 7 dias da primeira cobrança
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600">✓</span>
                            Primeira assinatura do plano Pro
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600">✓</span>
                            Problemas técnicos não resolvidos pelo suporte
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600">✓</span>
                            Cobrança duplicada ou erro de processamento
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-red-500/30 bg-red-500/5">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <XCircle className="h-6 w-6 text-red-600" aria-hidden="true" />
                          <h3 className="font-semibold text-red-600">Não Elegível</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <span className="text-red-600">✗</span>
                            Solicitação após 7 dias da cobrança
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600">✗</span>
                            Renovações automáticas (após o primeiro mês)
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600">✗</span>
                            Uso extensivo da plataforma (abuso)
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-600">✗</span>
                            Compra de leads extras ou add-ons
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* Como Solicitar */}
                <section aria-labelledby="como" className="mb-12">
                  <h2 id="como" className="text-2xl font-bold mb-6">Como Solicitar seu Reembolso</h2>
                  
                  <div className="space-y-4">
                    <div className="flex gap-4 items-start">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Envie um Email</h3>
                        <p className="text-muted-foreground text-sm">
                          Envie um email para <strong>financeiro@magnetlink.com.br</strong> com o 
                          assunto "Solicitação de Reembolso".
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4 items-start">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold flex-shrink-0">
                        2
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Informe seus Dados</h3>
                        <p className="text-muted-foreground text-sm">
                          Inclua seu email cadastrado, nome completo e, se possível, o motivo 
                          da solicitação (opcional, mas nos ajuda a melhorar).
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4 items-start">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold flex-shrink-0">
                        3
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Aguarde o Processamento</h3>
                        <p className="text-muted-foreground text-sm">
                          Analisaremos sua solicitação em até 3 dias úteis e confirmaremos 
                          por email.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4 items-start">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold flex-shrink-0">
                        4
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Receba seu Reembolso</h3>
                        <p className="text-muted-foreground text-sm">
                          O valor será estornado na mesma forma de pagamento em até 10 dias úteis 
                          após a aprovação.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Prazos */}
                <section aria-labelledby="prazos" className="mb-12 not-prose">
                  <h2 id="prazos" className="text-2xl font-bold mb-6">Prazos de Processamento</h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className="border-border/50 text-center">
                      <CardContent className="p-6">
                        <Clock className="h-8 w-8 text-primary mx-auto mb-3" aria-hidden="true" />
                        <h3 className="font-semibold mb-1">Análise</h3>
                        <p className="text-2xl font-bold text-primary">3 dias</p>
                        <p className="text-sm text-muted-foreground">úteis</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50 text-center">
                      <CardContent className="p-6">
                        <CreditCard className="h-8 w-8 text-primary mx-auto mb-3" aria-hidden="true" />
                        <h3 className="font-semibold mb-1">Estorno</h3>
                        <p className="text-2xl font-bold text-primary">10 dias</p>
                        <p className="text-sm text-muted-foreground">úteis</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50 text-center">
                      <CardContent className="p-6">
                        <RefreshCw className="h-8 w-8 text-primary mx-auto mb-3" aria-hidden="true" />
                        <h3 className="font-semibold mb-1">PIX/Boleto</h3>
                        <p className="text-2xl font-bold text-primary">5 dias</p>
                        <p className="text-sm text-muted-foreground">úteis</p>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* Cancelamento */}
                <section aria-labelledby="cancelamento" className="mb-12">
                  <h2 id="cancelamento" className="text-2xl font-bold mb-4">Cancelamento vs Reembolso</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    É importante entender a diferença:
                  </p>
                  
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/2 p-6 rounded-lg bg-muted/30 border border-border">
                      <h3 className="font-semibold mb-2">Cancelamento</h3>
                      <p className="text-sm text-muted-foreground">
                        Você pode cancelar sua assinatura a qualquer momento no painel. 
                        O acesso Pro será mantido até o final do período já pago, sem 
                        novas cobranças.
                      </p>
                    </div>
                    <div className="md:w-1/2 p-6 rounded-lg bg-muted/30 border border-border">
                      <h3 className="font-semibold mb-2">Reembolso</h3>
                      <p className="text-sm text-muted-foreground">
                        É a devolução do valor pago. Disponível apenas nos primeiros 
                        7 dias da primeira cobrança. Após o reembolso, o acesso Pro 
                        é removido imediatamente.
                      </p>
                    </div>
                  </div>
                </section>

                {/* FAQ */}
                <section aria-labelledby="faq" className="mb-12">
                  <h2 id="faq" className="text-2xl font-bold mb-6">Perguntas Frequentes</h2>
                  
                  <div className="space-y-4">
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex gap-3">
                          <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div>
                            <h3 className="font-semibold mb-1">Perco meus dados se pedir reembolso?</h3>
                            <p className="text-sm text-muted-foreground">
                              Não. Seus formulários e dados permanecem na conta, mas você volta 
                              para o plano gratuito com suas limitações.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex gap-3">
                          <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div>
                            <h3 className="font-semibold mb-1">Posso pedir reembolso mais de uma vez?</h3>
                            <p className="text-sm text-muted-foreground">
                              A garantia de 7 dias é válida apenas para a primeira assinatura. 
                              Renovações não são elegíveis para reembolso.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex gap-3">
                          <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div>
                            <h3 className="font-semibold mb-1">E se eu esqueci de cancelar antes da renovação?</h3>
                            <p className="text-sm text-muted-foreground">
                              Entre em contato conosco em até 48 horas após a cobrança. 
                              Analisaremos caso a caso e tentaremos ajudar.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* Contato */}
                <section aria-labelledby="contato" className="mb-12 not-prose">
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
                        <h2 id="contato" className="text-xl font-bold">Precisa de Ajuda?</h2>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Nossa equipe está pronta para ajudar com qualquer dúvida sobre 
                        reembolsos ou cancelamentos.
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Email:</strong> financeiro@magnetlink.com.br<br />
                        <strong>Horário:</strong> Segunda a sexta, 9h às 18h
                      </p>
                    </CardContent>
                  </Card>
                </section>

              </div>
            </div>
          </article>
        </main>

        <BlogFooter />
      </div>
    </>
  );
};

export default PoliticaReembolso;
